import template from './DefaultOnboardingChecklist.html';
import { homeName } from '..';
import { CHECKLIST_COMPLETED_TRACK_NAME, STEP_CHECKED_TRACK_NAME } from '../HomeSegmentEvents';
import { ReasonCodes } from 'types/ReasonCodes';
import { PromiseFactory } from 'modules/application/misc/PromiseFactory';
import { HomeConstants } from '../homeConstants';
import { HomeFeatures } from '../HomeFeatures';
var ChecklistNames;
(function (ChecklistNames) {
    ChecklistNames["UnderstandingBuilder"] = "understandingBuilder";
    ChecklistNames["ConnectingChannels"] = "connectingChannels";
    ChecklistNames["DeskCustomization"] = "deskCustomization";
    ChecklistNames["DeskActivation"] = "deskActivation";
    ChecklistNames["ReportCreation"] = "reportCreation";
    ChecklistNames["ArtificialIntelligence"] = "artificialIntelligence";
})(ChecklistNames || (ChecklistNames = {}));
export class DefaultOnboardingChecklistController {
    static $inject = ['UsersService', 'HistoryService', 'AnalyticsReportsService', 'AnalyticsChartsService', 'AccountService2', 'ApplicationService2', '$state', '$window', 'TranslateService', 'SegmentService', 'CurrentApplicationService'];
    constructor(UsersService, HistoryService, AnalyticsReportsService, AnalyticsChartsService, AccountService2, ApplicationService2, $state, $window, TranslateService, SegmentService, CurrentApplicationService) {
        'ngInject';
        this.UsersService = UsersService;
        this.HistoryService = HistoryService;
        this.AnalyticsReportsService = AnalyticsReportsService;
        this.AnalyticsChartsService = AnalyticsChartsService;
        this.AccountService2 = AccountService2;
        this.ApplicationService2 = ApplicationService2;
        this.$state = $state;
        this.$window = $window;
        this.TranslateService = TranslateService;
        this.SegmentService = SegmentService;
        this.CurrentApplicationService = CurrentApplicationService;
        this.showDeskCustomization = false;
        this.onboardingChecklistItems = Object.values(ChecklistNames);
        this.initializationPromise = PromiseFactory.new({});
        this.completedSteps = [];
    }
    async $onInit() {
        this.isCustomAIExperimentEnabled = await HomeFeatures.isCustomAIExperimentEnabled();
        this.applicationAccount = await this.ApplicationService2.getAccount();
        this.steps = await this.buildSteps();
        this.initializationPromise.resolve();
        this.trackChecklistCompletion();
        await this.saveCompletedStepsOnExtras();
    }
    async $onChanges(param) {
        await this.initializationPromise.promise;
        const { showDeskCustomization } = param;
        if (showDeskCustomization.currentValue) {
            const index = this.steps.findIndex(step => step.name === ChecklistNames.DeskActivation);
            if (index >= 0) {
                this.steps[index] = {
                    ...this.steps[index],
                    title: `onboardingChecklist.${ChecklistNames.DeskCustomization}.title`,
                    description: `onboardingChecklist.${ChecklistNames.DeskCustomization}.description`,
                };
            }
        }
    }
    async buildSteps() {
        await this.getStepsStatuses();
        let checklistNamesFiltered = this.getChecklistNamesExcept(ChecklistNames.DeskCustomization);
        if (!this.isCustomAIExperimentEnabled) {
            checklistNamesFiltered = checklistNamesFiltered.filter((value) => value !== ChecklistNames.ArtificialIntelligence);
        }
        return checklistNamesFiltered.map((name) => {
            return {
                name,
                title: `onboardingChecklist.${name}.title`,
                description: `onboardingChecklist.${name}.description`,
                checked: this.completedSteps.some(step => step === name),
                isLoading: this.checkIsLoading(name),
                isNew: this.checkIsNew(name),
                onClick: () => this.redirect(homeName, name),
            };
        });
    }
    checkIsNew(stepName) {
        if (stepName === ChecklistNames.ArtificialIntelligence &&
            this.customAiRequestState !== 'requested' &&
            this.customAiRequestState !== 'finished') {
            return true;
        }
        return false;
    }
    checkIsLoading(stepName) {
        if (stepName === ChecklistNames.ArtificialIntelligence &&
            this.customAiRequestState === 'requested') {
            return true;
        }
        return false;
    }
    get customAiRequestState() {
        return this.applicationAccount.extras.customAiResquestState;
    }
    getChecklistNamesExcept(ignoreName) {
        return this.onboardingChecklistItems.filter((value) => value !== ignoreName);
    }
    async getStepsStatuses() {
        try {
            const stepsPromises = [
                this.getBuilderUnderstandingStepStatus(),
                this.getChannelConnectionStepStatus(),
                this.getDeskUsageStepStatus(),
                this.getReportCreationStepStatus(),
                this.getArtificialIntelligenceStepStatus(),
            ];
            await Promise.all(stepsPromises);
        }
        catch (err) {
            console.error('An error has ocurred while verifing steps statuses', err);
        }
    }
    trackChecklistCompletion() {
        if (this.completedSteps.length !== this.steps.length) {
            return;
        }
        let stepsCompletedOnExtras = this.applicationAccount.extras.onboardingChecklist?.split(',').filter((step) => !!step);
        stepsCompletedOnExtras = stepsCompletedOnExtras ?? [];
        if (stepsCompletedOnExtras.length < this.steps.length) {
            this.createStepCompletionTrack(CHECKLIST_COMPLETED_TRACK_NAME, undefined);
        }
    }
    async getBuilderUnderstandingStepStatus() {
        const businessRule = async () => {
            const ignoreBotOwnerContactFilter = '&$filter=(source%20ne%20\'blip.ai\')';
            const { items } = await this.UsersService.getUsers(ignoreBotOwnerContactFilter, 0, 1);
            return items.length > 0;
        };
        await this.stepIsCompleted(ChecklistNames.UnderstandingBuilder, businessRule);
    }
    async getChannelConnectionStepStatus() {
        const businessRule = async () => {
            const application = await this.CurrentApplicationService.fetchApplication();
            const sessionStorageKey = `${application.shortName}-${ChecklistNames.ConnectingChannels}`;
            const completion = this.$window.sessionStorage.getItem(sessionStorageKey);
            return completion === HomeConstants.COMPLETED_STATUS;
        };
        await this.stepIsCompleted(ChecklistNames.ConnectingChannels, businessRule);
    }
    async getDeskUsageStepStatus() {
        const businessRule = async () => {
            const { items } = await this.HistoryService.get(undefined, 0, 1);
            return items.length > 0;
        };
        await this.stepIsCompleted(ChecklistNames.DeskActivation, businessRule);
    }
    async getReportCreationStepStatus() {
        const businessRule = async () => {
            const { email } = await this.AccountService2.me();
            const userIdentity = `${encodeURIComponent(email)}@blip.ai`;
            const { reports } = await this.AnalyticsReportsService.getMany(userIdentity);
            if (reports[0] != undefined) {
                const { charts } = await this.AnalyticsChartsService.getMany(reports[0].id, 0, 1);
                return charts.length > 0;
            }
            return false;
        };
        await this.stepIsCompleted(ChecklistNames.ReportCreation, businessRule);
    }
    async getArtificialIntelligenceStepStatus() {
        const businessRule = async () => {
            if (!this.applicationAccount) {
                this.applicationAccount = await this.ApplicationService2.getAccount();
            }
            return this.applicationAccount.extras.customAiResquestState === 'finished';
        };
        await this.stepIsCompleted(ChecklistNames.ArtificialIntelligence, businessRule);
    }
    async stepIsCompleted(checklistName, businessRule) {
        try {
            const isCompletedOnExtras = await this.stepIsCompletedOnExtras(checklistName);
            if (isCompletedOnExtras) {
                this.completedSteps.push(checklistName);
                return;
            }
            const isCompletedOnBusinessRule = await businessRule();
            if (isCompletedOnBusinessRule) {
                this.completedSteps.push(checklistName);
                this.createStepCompletionTrack(STEP_CHECKED_TRACK_NAME, checklistName);
            }
        }
        catch (err) {
            if (err.code === ReasonCodes.ResourceNotFound) {
                return;
            }
            console.error(`An error has ocurred while verifing the status of step ${checklistName}`, err);
        }
    }
    async stepIsCompletedOnExtras(step) {
        if (!this.applicationAccount) {
            this.applicationAccount = await this.ApplicationService2.getAccount();
        }
        return this.applicationAccount.extras.onboardingChecklist?.includes(step);
    }
    async saveCompletedStepsOnExtras() {
        const completedSteps = [...new Set(this.completedSteps)].toString();
        const completedStepsOnExtras = this.applicationAccount.extras?.onboardingChecklist;
        if (completedSteps === completedStepsOnExtras) {
            return;
        }
        const extras = { ...this.applicationAccount.extras, onboardingChecklist: completedSteps };
        await this.ApplicationService2.setAccount({ ...this.applicationAccount, extras });
    }
    redirect(state, stepName) {
        const appCuesId = this.TranslateService.instantTranslate(`onboardingChecklist.${stepName}.appCuesId`);
        this.$state.go(state, { appcue: appCuesId });
    }
    createStepCompletionTrack(name, step) {
        const type = this.showDeskCustomization ? 'desk' : 'empty';
        this.SegmentService.createApplicationTrack({
            trackEvent: name,
            payload: { type, step },
        });
    }
}
DefaultOnboardingChecklistController.$inject = ['UsersService', 'HistoryService', 'AnalyticsReportsService', 'AnalyticsChartsService', 'AccountService2', 'ApplicationService2', '$state', '$window', 'TranslateService', 'SegmentService', 'CurrentApplicationService'];
export const DefaultOnboardingChecklistComponent = {
    controller: DefaultOnboardingChecklistController,
    controllerAs: '$ctrl',
    template,
    bindings: {
        showDeskCustomization: '<',
    },
};
